import React from "react";
import type { ReactNode } from "react";
import ReactBSAlert from "react-bootstrap-sweetalert";

/**
 * The delete confirmation this app had copy-pasted into 20 view files — 23
 * occurrences on premaster @ 979c175f (SCRUM-1356).
 *
 * It owns two things every copy re-implemented. The markup, including the
 * `swal2-*` classes and the "Ok" link the integration suites select on, and the
 * `@ts-expect-error` the library's prop-types force. And the ORDER of the
 * confirm handler: `onSuccess` runs only after `onConfirm` has resolved. That
 * ordering is the bug class SCRUM-840, SCRUM-1017/1018, SCRUM-1021 and
 * SCRUM-1033 each had to repair in their own copy — a handler that toasted
 * success right after firing the delete reported success on a *failed* delete.
 *
 * The success/error CHANNEL deliberately stays at the call site: the screens
 * disagree today (`NotificationAlertOptions` on Tags/Clients, `setAppMessage` on
 * Sites) and unifying them is a behaviour decision, not this refactor.
 *
 * `onClose` runs before the delete is awaited, which is what every copy did —
 * the dialog disappears on click, not when the request comes back.
 */
export type ConfirmDeleteProps = {
  /** the question in the dialog head, e.g. "Would you like to remove this Tag?" */
  title: string;
  /** clears the alert; called before `onConfirm` */
  onClose: () => void;
  /**
   * performs the delete; must REJECT on failure, so a `createAppAsyncThunk` has to be
   * handed over as `dispatch(thunk()).unwrap()`
   */
  onConfirm: () => unknown;
  /** called only after `onConfirm` resolves */
  onSuccess?: () => void;
  /**
   * called when `onConfirm` rejects; omit it to leave the failure to the central error
   * hub, which is what these screens do
   */
  onError?: (err: unknown) => void;
  /** body text under the question */
  children?: ReactNode;
  /**
   * also render an explicit "Cancel" control (SCRUM-1303); off by default, so the screens
   * that predate it are unchanged — there the overlay and Escape are the only way out, as
   * they always were
   */
  showCancel?: boolean;
};

export type SweetAlertBaseProps = {
  title: string;
  customIcon: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  confirmBtnText: string;
  showCancel?: boolean;
  cancelBtnText?: string;
  children?: ReactNode;
};

/**
 * The `react-bootstrap-sweetalert` markup both dialogs here share, with the one
 * `@ts-expect-error` the library's types force, so a dialog built on it adds no
 * copy of that suppression (SCRUM-2352; SCRUM-2114 owns the other copies).
 */
export const SweetAlertBase = ({
  title,
  customIcon,
  onConfirm,
  onCancel,
  confirmBtnText,
  showCancel = false,
  cancelBtnText,
  children,
}: SweetAlertBaseProps) => (
  // @ts-expect-error react-bootstrap-sweetalert prop-types (customIcon/btnSize)
  <ReactBSAlert
    custom
    style={{ display: "block", marginTop: "-100px" }}
    title={title}
    customIcon={customIcon}
    onConfirm={onConfirm}
    onCancel={onCancel}
    confirmBtnBsStyle="default"
    confirmBtnText={confirmBtnText}
    showCancel={showCancel}
    cancelBtnText={cancelBtnText}
    btnSize=""
  >
    {children}
  </ReactBSAlert>
);

const ConfirmDelete = ({
  title,
  onClose,
  onConfirm,
  onSuccess,
  onError,
  children,
  showCancel = false,
}: ConfirmDeleteProps) => {
  const handleConfirm = async () => {
    onClose();
    try {
      await onConfirm();
      onSuccess?.();
    } catch (err) {
      onError?.(err);
    }
  };

  return (
    <SweetAlertBase
      title={title}
      customIcon={
        <div
          className="swal2-icon swal2-question swal2-animate-question-icon"
          style={{ display: "flex" }}
        >
          <span className="swal2-icon-text">?</span>
        </div>
      }
      onConfirm={handleConfirm}
      onCancel={onClose}
      confirmBtnText="Ok"
      showCancel={showCancel}
      cancelBtnText="Cancel"
    >
      {children}
    </SweetAlertBase>
  );
};

export default ConfirmDelete;
